import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, errMsg } from "../api";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "../components/ui/tooltip";
import {
    Paperclip, Calendar as CalIcon, Trash2, Send, Plus, Download, Activity, MessageSquare, Edit3, Save, Info, Loader2, CornerUpLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import MentionTextarea from "./MentionTextarea";
import DropZone from "./DropZone";

const STATUSES = ["created", "taken", "working", "completed"];
const STATUS_STYLES = {
    created: "bg-slate-100 text-slate-700 border-slate-200",
    taken: "bg-blue-100 text-blue-800 border-blue-200",
    working: "bg-amber-100 text-amber-800 border-amber-200",
    completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const ACCEPTED_FORMATS = ["JPG", "PNG", "GIF", "WEBP", "PDF", "DOCX", "PPTX", "XLSX", "CSV", "TXT"];

function formatDateTimeLocal(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function TaskDetailDialog({ taskId, open, onClose, onChange, allUsers, spaces, onOpenTask }) {
    const { user, lang } = useAuth();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [parentTask, setParentTask] = useState(null);
    const [editingTitle, setEditingTitle] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [titleVal, setTitleVal] = useState("");
    const [descVal, setDescVal] = useState("");
    const [comment, setComment] = useState("");
    const [commentMentions, setCommentMentions] = useState([]);
    const [commentSpokenAt, setCommentSpokenAt] = useState("");
    const [showSpokenAt, setShowSpokenAt] = useState(false);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
    const [subtaskAssignees, setSubtaskAssignees] = useState([]);
    const [showAddSubtask, setShowAddSubtask] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const load = useCallback(async () => {
        if (!taskId) return;
        try {
            const { data } = await api.get(`/tasks/${taskId}`);
            setTask(data);
            setTitleVal(data.title);
            setDescVal(data.description || "");
            if (data.parent_task_id) {
                try {
                    const { data: p } = await api.get(`/tasks/${data.parent_task_id}`);
                    setParentTask(p);
                } catch { /* parent task not found, ignore */ }
            } else {
                setParentTask(null);
            }
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) {
                toast.error("Görev bulunamadı veya silinmiş");
                onClose && onClose();
            } else {
                toast.error("Error loading task");
            }
        }
    }, [taskId, onClose]);

    useEffect(() => {
        if (open) {
            load();
        } else {
            // Reset state when dialog closes so reopening starts fresh
            setTask(null);
            setParentTask(null);
            setEditingTitle(false);
            setEditingDesc(false);
            setComment("");
        }
    }, [open, taskId, load]);

    if (!open) return null;

    const space = spaces.find((s) => s.id === task?.space_id);
    // Show ALL users (not just space members) so admin can assign external helpers (e.g. IT person for a Sales task).
    // Space members come first, external "guests" at the bottom with a visual badge.
    const eligibleAssignees = (() => {
        if (!space) return allUsers;
        const memberIds = new Set(space.member_ids || []);
        const members = allUsers.filter((u) => memberIds.has(u.id) || u.role === "admin");
        const externals = allUsers.filter((u) => !memberIds.has(u.id) && u.role !== "admin");
        return [...members, ...externals];
    })();
    const isExternalAssignee = (uid) => space && !(space.member_ids || []).includes(uid) && allUsers.find((u) => u.id === uid)?.role !== "admin";

    const updateField = async (patch, silent = false) => {
        try {
            const { data } = await api.patch(`/tasks/${taskId}`, patch);
            setTask({ ...task, ...data });
            if (!silent) toast.success(t(lang, "save"));
            onChange && onChange();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const handleStatus = (s) => updateField({ status: s }, true);
    const handlePriority = (p) => updateField({ priority: p }, true);
    const handleDeadline = (val) => updateField({ deadline: val ? new Date(val).toISOString() : null }, true);

    const toggleAssignee = (uid) => {
        const current = task.assignee_ids || [];
        const next = current.includes(uid) ? current.filter((x) => x !== uid) : [...current, uid];
        updateField({ assignee_ids: next }, true);
    };

    const saveTitle = () => {
        if (titleVal.trim() && titleVal !== task.title) updateField({ title: titleVal }, true);
        setEditingTitle(false);
    };

    const saveDesc = () => {
        if (descVal !== task.description) updateField({ description: descVal }, true);
        setEditingDesc(false);
    };

    const addComment = async () => {
        if (!comment.trim()) return;
        try {
            await api.post(`/tasks/${taskId}/comments`, {
                text: comment,
                mentioned_user_ids: commentMentions,
                spoken_at: commentSpokenAt ? new Date(commentSpokenAt).toISOString() : null,
            });
            setComment("");
            setCommentMentions([]);
            setCommentSpokenAt("");
            setShowSpokenAt(false);
            load();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const editComment = async (commentId, text, spokenAt) => {
        try {
            await api.patch(`/tasks/${taskId}/comments/${commentId}`, {
                text, spoken_at: spokenAt || null,
            });
            load();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const uploadFileObject = async (file) => {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        setUploadProgress(0);
        try {
            await api.post(`/tasks/${taskId}/files`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (evt.total) {
                        setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
                    }
                },
            });
            toast.success(t(lang, "uploadFile"));
            load();
        } catch (err) {
            toast.error(errMsg(err, "Upload failed"));
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const uploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await uploadFileObject(file);
        e.target.value = "";
    };

    const downloadFile = (file) => {
        const token = localStorage.getItem("taskflow_token");
        const url = `${API}/files/${file.id}/download?auth=${encodeURIComponent(token)}`;
        window.open(url, "_blank");
    };

    const deleteTask = async () => {
        if (!window.confirm(t(lang, "confirmDelete"))) return;
        try {
            await api.delete(`/tasks/${taskId}`);
            toast.success(t(lang, "delete"));
            onChange && onChange();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const addSubtask = async () => {
        if (!newSubtaskTitle.trim()) return;
        try {
            await api.post("/tasks", {
                space_id: task.space_id,
                parent_task_id: task.id,
                title: newSubtaskTitle,
                priority: "medium",
                assignee_ids: subtaskAssignees,
            });
            setNewSubtaskTitle("");
            setSubtaskAssignees([]);
            setShowAddSubtask(false);
            load();
            toast.success(t(lang, "create"));
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const isAdmin = user?.role === "admin";

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="task-detail-dialog" className="max-w-4xl w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col gap-0">
                {task && (
                    <>
                        <DialogHeader className="px-5 sm:px-6 py-4 border-b border-neutral-200 bg-white space-y-2 text-left">
                            {/* Parent link if subtask */}
                            {parentTask && (
                                <button
                                    onClick={() => onOpenTask && onOpenTask(parentTask.id)}
                                    data-testid="parent-task-link"
                                    className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900 self-start"
                                >
                                    <CornerUpLeft className="w-3 h-3" />
                                    <span className="uppercase tracking-wider font-semibold">{t(lang, "parentTask")}:</span>
                                    <span className="font-medium text-neutral-700 truncate max-w-[300px]">{parentTask.title}</span>
                                </button>
                            )}

                            <div className="flex items-center gap-2 flex-wrap">
                                {/* Status badge prominently */}
                                <Badge variant="outline" className={`uppercase tracking-wider text-[10px] font-bold ${STATUS_STYLES[task.status]}`} data-testid={`detail-status-badge-${task.status}`}>
                                    {t(lang, task.status)}
                                </Badge>
                                <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                                    {space?.name || "Space"} · #{task.id.slice(0, 6)}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 pr-8">
                                {editingTitle ? (
                                    <div className="flex items-center gap-2 flex-1">
                                        <Input
                                            data-testid="task-title-input"
                                            value={titleVal}
                                            onChange={(e) => setTitleVal(e.target.value)}
                                            className="text-xl font-display font-bold tracking-tight"
                                            autoFocus
                                            onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditingTitle(false); }}
                                            onBlur={saveTitle}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 group/title flex-1 min-w-0">
                                        <DialogTitle className="font-display text-xl sm:text-2xl font-bold tracking-tight truncate">
                                            {task.title}
                                        </DialogTitle>
                                        <button
                                            onClick={() => setEditingTitle(true)}
                                            data-testid="edit-title-btn"
                                            className="opacity-0 group-hover/title:opacity-100 transition text-neutral-500 hover:text-neutral-900 shrink-0"
                                        >
                                            <Edit3 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            <DialogDescription className="text-[11px] text-neutral-500 mt-0.5">
                                {t(lang, "createdBy")}: <span className="text-neutral-700 font-medium">{task.created_by_name}</span> ·{" "}
                                {t(lang, "createdAt")}: <span className="text-neutral-700 font-medium">{new Date(task.created_at).toLocaleString(LOCALES[lang])}</span>
                            </DialogDescription>
                        </DialogHeader>

                        {/* Department-task claim banner */}
                        {task.assigned_to_space && (
                            <div
                                data-testid="dept-task-banner"
                                className={`mx-5 sm:mx-6 mt-3 p-3 rounded-md border flex items-center justify-between gap-3 ${task.claimed_by ? "bg-neutral-50 border-neutral-200" : "bg-blue-50 border-blue-200"}`}
                            >
                                <div className="text-xs">
                                    <span className="font-semibold text-blue-900 inline-flex items-center gap-1.5">
                                        🏢 {t(lang, "departmentTask")}
                                    </span>
                                    {task.claimed_by ? (
                                        <span className="text-neutral-600 ml-2">
                                            · {t(lang, "claimedBy")}{" "}
                                            <span className="font-semibold">
                                                {allUsers.find((u) => u.id === task.claimed_by)?.name || "—"}
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-blue-700/80 ml-2">
                                            {lang === "de" ? "noch frei — übernimm sie!" : lang === "tr" ? "henüz alınmadı" : "open — claim it!"}
                                        </span>
                                    )}
                                </div>
                                {!task.claimed_by && (
                                    <Button
                                        size="sm"
                                        data-testid="claim-task-btn"
                                        onClick={async () => {
                                            try {
                                                const { data: claimed } = await api.post(`/tasks/${task.id}/claim`);
                                                toast.success(t(lang, "claimTask"));
                                                // If this task came from a mail, jump directly to that mail.
                                                if (claimed?.source === "ms_mail" && claimed?.ms_message_id && claimed?.ms_mailbox) {
                                                    const mbParam = claimed.ms_mailbox.endsWith("@personal") ? "me" : claimed.ms_mailbox;
                                                    // Close the dialog first then navigate via react-router so TabPagesRouter switches the tab
                                                    onClose && onClose();
                                                    setTimeout(() => navigate(`/mail?msg=${encodeURIComponent(claimed.ms_message_id)}&mailbox=${encodeURIComponent(mbParam)}`), 100);
                                                    return;
                                                }
                                                load();
                                                onChange && onChange();
                                            } catch (e) {
                                                toast.error(errMsg(e, "Error"));
                                            }
                                        }}
                                        className="bg-blue-600 hover:bg-blue-700 h-8"
                                    >
                                        ✋ {t(lang, "claimTask")}
                                    </Button>
                                )}
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                                {/* Main */}
                                <div className="md:col-span-2 p-5 sm:p-6 space-y-6 md:border-r border-neutral-200">
                                    {/* Description */}
                                    <section>
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">{t(lang, "description")}</h4>
                                            {!editingDesc && (
                                                <button
                                                    onClick={() => setEditingDesc(true)}
                                                    data-testid="edit-desc-btn"
                                                    className="text-neutral-500 hover:text-neutral-900 text-xs flex items-center gap-1"
                                                >
                                                    <Edit3 className="w-3 h-3" /> {t(lang, "edit")}
                                                </button>
                                            )}
                                        </div>
                                        {editingDesc ? (
                                            <div className="space-y-2">
                                                <Textarea
                                                    data-testid="task-desc-input"
                                                    value={descVal}
                                                    onChange={(e) => setDescVal(e.target.value)}
                                                    rows={5}
                                                />
                                                <div className="flex gap-2">
                                                    <Button size="sm" onClick={saveDesc} data-testid="save-desc-btn">
                                                        <Save className="w-3 h-3 mr-1" />{t(lang, "save")}
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => { setDescVal(task.description || ""); setEditingDesc(false); }}>
                                                        {t(lang, "cancel")}
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap break-words">
                                                {task.description || <span className="text-neutral-400 italic">—</span>}
                                            </p>
                                        )}
                                    </section>

                                    <Separator />

                                    {/* Subtasks */}
                                    {!task.parent_task_id && (
                                        <>
                                            <section>
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
                                                        {t(lang, "subtasks")} ({(task.subtasks || []).length})
                                                    </h4>
                                                    <button
                                                        onClick={() => setShowAddSubtask((v) => !v)}
                                                        data-testid="add-subtask-btn"
                                                        className="text-xs flex items-center gap-1 text-neutral-700 hover:text-neutral-900"
                                                    >
                                                        <Plus className="w-3 h-3" /> {t(lang, "addSubtask")}
                                                    </button>
                                                </div>
                                                {showAddSubtask && (
                                                    <div className="mb-3 p-3 rounded-md border border-dashed border-neutral-300 bg-neutral-50 space-y-2" data-testid="add-subtask-form">
                                                        <Input
                                                            data-testid="subtask-title-input"
                                                            placeholder={t(lang, "title")}
                                                            value={newSubtaskTitle}
                                                            onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                                            onKeyDown={(e) => e.key === "Enter" && addSubtask()}
                                                        />
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {eligibleAssignees.map((u) => {
                                                                const isGuest = isExternalAssignee(u.id);
                                                                const active = subtaskAssignees.includes(u.id);
                                                                return (
                                                                    <button
                                                                        key={u.id}
                                                                        onClick={() => setSubtaskAssignees((prev) => prev.includes(u.id) ? prev.filter((x) => x !== u.id) : [...prev, u.id])}
                                                                        className={`px-2 py-1 rounded-md text-xs border flex items-center gap-1 ${
                                                                            active
                                                                                ? (isGuest ? "bg-amber-600 text-white border-amber-700" : "bg-neutral-900 text-white border-neutral-900")
                                                                                : (isGuest ? "border-amber-300 text-amber-800 bg-amber-50" : "border-neutral-300 text-neutral-700")
                                                                        }`}
                                                                        data-testid={`subtask-assignee-${u.id}`}
                                                                    >
                                                                        {u.name}
                                                                        {isGuest && <span className="text-[9px] font-bold opacity-80">Gast</span>}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <Button size="sm" onClick={addSubtask} data-testid="subtask-save-btn">{t(lang, "create")}</Button>
                                                            <Button size="sm" variant="ghost" onClick={() => setShowAddSubtask(false)}>{t(lang, "cancel")}</Button>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="space-y-1.5">
                                                    {(task.subtasks || []).length === 0 && (
                                                        <p className="text-xs text-neutral-400 italic">—</p>
                                                    )}
                                                    {(task.subtasks || []).map((st) => (
                                                        <SubtaskRow
                                                            key={st.id}
                                                            st={st}
                                                            allUsers={allUsers}
                                                            onOpen={() => onOpenTask && onOpenTask(st.id)}
                                                            onToggleComplete={async () => {
                                                                try {
                                                                    await api.patch(`/tasks/${st.id}`, {
                                                                        status: st.status === "completed" ? "created" : "completed",
                                                                    });
                                                                    load();
                                                                    onChange && onChange();
                                                                } catch (e) {
                                                                    toast.error(errMsg(e, "Error"));
                                                                }
                                                            }}
                                                            lang={lang}
                                                        />
                                                    ))}
                                                </div>
                                            </section>

                                            <Separator />
                                        </>
                                    )}

                                    {/* Attachments */}
                                    <section>
                                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
                                                {t(lang, "attachments")} ({(task.attachments || []).length})
                                            </h4>
                                            <div className="flex items-center gap-2">
                                                <TooltipProvider delayDuration={150}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button data-testid="upload-info-btn" className="text-neutral-500 hover:text-neutral-900">
                                                                <Info className="w-3.5 h-3.5" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top" className="max-w-xs">
                                                            <p className="text-xs">
                                                                <span className="font-semibold">{t(lang, "acceptedFormats")}:</span>{" "}
                                                                {ACCEPTED_FORMATS.join(", ")}
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                                <label
                                                    className={`text-xs flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-300 hover:bg-neutral-50 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                                                    data-testid="upload-file-label"
                                                >
                                                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                                                    {uploading ? `${t(lang, "uploading")} ${uploadProgress}%` : t(lang, "uploadFile")}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.docx,.pptx,.xlsx,.csv,.txt"
                                                        onChange={uploadFile}
                                                        data-testid="upload-file-input"
                                                        disabled={uploading}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                        {uploading && (
                                            <div className="mb-2 h-1 bg-neutral-100 rounded overflow-hidden" data-testid="upload-progress-bar">
                                                <div className="h-full bg-neutral-900 transition-all" style={{ width: `${uploadProgress}%` }} />
                                            </div>
                                        )}
                                        <DropZone
                                            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.docx,.pptx,.xlsx,.csv,.txt"
                                            onFile={uploadFileObject}
                                            disabled={uploading}
                                            compact
                                            label={lang === "de" ? "Datei hierher ziehen, Str+V einfügen oder klicken" : "Drop file here, paste with Ctrl+V, or click"}
                                            testId="task-dropzone"
                                            className="mb-2"
                                        />
                                        <div className="space-y-1.5">
                                            {(task.attachments || []).length === 0 && !uploading && (
                                                <p className="text-xs text-neutral-400 italic">—</p>
                                            )}
                                            {(task.attachments || []).map((f) => (
                                                <div key={f.id} className="flex items-center gap-2 p-2 rounded-md border border-neutral-200 bg-neutral-50/50">
                                                    <Paperclip className="w-3.5 h-3.5 text-neutral-500 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{f.original_filename}</p>
                                                        <p className="text-[11px] text-neutral-500">{(f.size / 1024).toFixed(1)} KB · {f.uploaded_by_name}</p>
                                                    </div>
                                                    <button
                                                        onClick={() => downloadFile(f)}
                                                        className="text-neutral-600 hover:text-neutral-900 p-1"
                                                        data-testid={`download-file-${f.id}`}
                                                    >
                                                        <Download className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </section>

                                    <Separator />

                                    {/* Comments */}
                                    <section>
                                        <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-3 flex items-center gap-1.5">
                                            <MessageSquare className="w-3.5 h-3.5" /> {t(lang, "comments")} ({(task.comments || []).length})
                                        </h4>
                                        <div className="space-y-3 mb-3">
                                            {(task.comments || []).map((c) => (
                                                <CommentItem
                                                    key={c.id}
                                                    c={c}
                                                    lang={lang}
                                                    canEdit={c.user_id === user?.id || user?.role === "admin" || user?.role === "master"}
                                                    onSave={(text, spokenAt) => editComment(c.id, text, spokenAt)}
                                                />
                                            ))}
                                        </div>
                                        <div className="flex gap-2 items-stretch">
                                            <div className="flex-1 space-y-1.5">
                                                <MentionTextarea
                                                    value={comment}
                                                    onChange={setComment}
                                                    onSubmit={addComment}
                                                    placeholder={t(lang, "addComment")}
                                                    users={allUsers}
                                                    mentionAllLabel={t(lang, "mentionAll")}
                                                    testId="comment-input"
                                                    onMentionsChange={(ids) => setCommentMentions(ids)}
                                                />
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {!showSpokenAt && !commentSpokenAt ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowSpokenAt(true)}
                                                            data-testid="add-spoken-at-btn"
                                                            className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-dashed border-neutral-300 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
                                                        >
                                                            <CalIcon className="w-3 h-3" /> {t(lang, "addSpokenAt")}
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5">
                                                            <CalIcon className="w-3 h-3 text-neutral-500" />
                                                            <Input
                                                                type="date"
                                                                data-testid="comment-spoken-at-input"
                                                                value={commentSpokenAt}
                                                                onChange={(e) => setCommentSpokenAt(e.target.value)}
                                                                className="h-8 w-[155px] text-xs"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => { setCommentSpokenAt(""); setShowSpokenAt(false); }}
                                                                className="text-neutral-500 hover:text-red-600 text-[10px] underline"
                                                                data-testid="clear-spoken-at-btn"
                                                            >
                                                                {t(lang, "cancel")}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <Button onClick={addComment} disabled={!comment.trim()} data-testid="comment-send-btn" className="bg-neutral-900 hover:bg-neutral-800 h-auto px-3 self-stretch shrink-0">
                                                <Send className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </section>
                                </div>

                                {/* Sidebar */}
                                <div className="p-5 sm:p-6 bg-neutral-50/40 space-y-5 border-t md:border-t-0 border-neutral-200">
                                    <section>
                                        <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2">{t(lang, "status")}</h4>
                                        <Select value={task.status} onValueChange={handleStatus}>
                                            <SelectTrigger className="w-full" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {STATUSES.map((s) => (
                                                    <SelectItem key={s} value={s} data-testid={`status-${s}`}>{t(lang, s)}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </section>

                                    <section>
                                        <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2">{t(lang, "priority")}</h4>
                                        <Select value={task.priority} onValueChange={handlePriority}>
                                            <SelectTrigger className="w-full" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="low" data-testid="priority-low">{t(lang, "low")}</SelectItem>
                                                <SelectItem value="medium" data-testid="priority-medium">{t(lang, "medium")}</SelectItem>
                                                <SelectItem value="high" data-testid="priority-high">{t(lang, "high")}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </section>

                                    <section>
                                        <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2 flex items-center gap-1">
                                            <CalIcon className="w-3 h-3" />{t(lang, "deadline")}
                                        </h4>
                                        <Input
                                            type="datetime-local"
                                            data-testid="task-deadline-input"
                                            value={formatDateTimeLocal(task.deadline)}
                                            onChange={(e) => handleDeadline(e.target.value)}
                                        />
                                    </section>

                                    <section>
                                        <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2">
                                            {t(lang, "price")} <span className="font-normal lowercase text-neutral-400">({lang === "tr" ? "opsiyonel" : "optional"})</span>
                                        </h4>
                                        <PriceEditor task={task} lang={lang} onSave={updateField} />
                                    </section>

                                    <section>
                                        <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2">{t(lang, "assignees")}</h4>
                                        <div className="flex flex-wrap gap-1.5">
                                            {eligibleAssignees.map((u) => {
                                                const active = (task.assignee_ids || []).includes(u.id);
                                                const isGuest = isExternalAssignee(u.id);
                                                return (
                                                    <button
                                                        key={u.id}
                                                        onClick={() => toggleAssignee(u.id)}
                                                        data-testid={`task-assignee-${u.id}`}
                                                        title={isGuest ? (lang === "de" ? "Nicht in dieser Abteilung — wird als Gast zugewiesen" : "Not in this department — assigned as guest") : undefined}
                                                        className={`px-2 py-1 rounded-md text-xs border flex items-center gap-1 ${
                                                            active
                                                                ? (isGuest ? "bg-amber-600 text-white border-amber-700" : "bg-neutral-900 text-white border-neutral-900")
                                                                : (isGuest ? "border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100" : "border-neutral-300 text-neutral-700 hover:bg-neutral-100")
                                                        }`}
                                                    >
                                                        {u.name}
                                                        {isGuest && <span className="text-[9px] font-bold opacity-80">Gast</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <p className="text-[10px] text-neutral-500 mt-1.5">
                                            {lang === "de" ? "💡 Nicht-Mitglieder können als Gast zugewiesen werden (z.B. IT-Hilfe für ein Sales-Task)." : "💡 Non-members can be assigned as guests (e.g. IT helping a Sales task)."}
                                        </p>
                                        {/* Department assignment toggle */}
                                        <button
                                            onClick={() => updateField({ assigned_to_space: !task.assigned_to_space }, true)}
                                            data-testid="task-toggle-space-assignment"
                                            className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold border transition ${
                                                task.assigned_to_space
                                                    ? "bg-blue-50 border-blue-300 text-blue-800"
                                                    : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                                            }`}
                                        >
                                            {task.assigned_to_space
                                                ? (lang === "de" ? "✓ An Abteilung zugewiesen" : "✓ Assigned to department")
                                                : (lang === "de" ? "An gesamte Abteilung zuweisen" : "Assign to whole department")}
                                        </button>
                                    </section>

                                    <Separator />

                                    <section>
                                        <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2 flex items-center gap-1.5">
                                            <Activity className="w-3 h-3" />{t(lang, "activity")}
                                        </h4>
                                        <div className="border border-neutral-200 rounded-md bg-white">
                                            <ScrollArea className="h-48" data-testid="activity-scroll">
                                                <ul className="p-2 space-y-2">
                                                    {(task.activity || []).slice().reverse().map((a) => (
                                                        <li key={a.id} className="text-[11px] text-neutral-600 leading-relaxed">
                                                            <span className="font-semibold text-neutral-800">{a.user_name}</span>{" "}
                                                            <span>{a.action}</span>
                                                            {a.details && <span className="text-neutral-500"> — {a.details}</span>}
                                                            <span className="block text-neutral-400 text-[10px]">{new Date(a.timestamp).toLocaleString(LOCALES[lang])}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </ScrollArea>
                                        </div>
                                    </section>

                                    {isAdmin && (
                                        <>
                                            <Separator />
                                            <Button
                                                variant="destructive"
                                                className="w-full"
                                                onClick={deleteTask}
                                                data-testid="task-delete-btn"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" /> {t(lang, "delete")}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function renderCommentText(text) {
    // Highlight @mentions
    const parts = text.split(/(@\w+)/g);
    return parts.map((p, i) =>
        p.startsWith("@") ? (
            <span key={i} className="bg-amber-100 text-amber-900 rounded px-1 font-medium">{p}</span>
        ) : (
            <React.Fragment key={i}>{p}</React.Fragment>
        )
    );
}

function CommentItem({ c, lang, canEdit, onSave }) {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(c.text);
    const [spokenAt, setSpokenAt] = useState(c.spoken_at ? c.spoken_at.slice(0, 10) : "");

    useEffect(() => {
        setText(c.text);
        setSpokenAt(c.spoken_at ? c.spoken_at.slice(0, 10) : "");
    }, [c.id, c.text, c.spoken_at]);

    const save = () => {
        onSave(text, spokenAt ? new Date(spokenAt).toISOString() : null);
        setEditing(false);
    };

    return (
        <div className="flex gap-2.5" data-testid={`comment-${c.id}`}>
            <div className="w-7 h-7 shrink-0 rounded-full bg-neutral-900 text-white text-[10px] font-semibold flex items-center justify-center">
                {c.user_name?.slice(0, 2)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs">
                    <span className="font-semibold">{c.user_name}</span>
                    <span className="text-neutral-500 ml-2">{new Date(c.timestamp).toLocaleString(LOCALES[lang])}</span>
                    {c.edited_at && <span className="text-neutral-400 ml-1 italic text-[10px]">· {lang === "de" ? "bearb." : lang === "tr" ? "düz." : "edited"}</span>}
                    {canEdit && !editing && (
                        <button
                            onClick={() => setEditing(true)}
                            data-testid={`edit-comment-${c.id}`}
                            className="ml-2 text-neutral-500 hover:text-neutral-900 underline text-[10px]"
                        >
                            {t(lang, "edit")}
                        </button>
                    )}
                </p>
                {c.spoken_at && !editing && (
                    <p className="text-[11px] text-amber-800 bg-amber-50 inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded border border-amber-200" data-testid={`spoken-at-${c.id}`}>
                        <CalIcon className="w-2.5 h-2.5" />
                        <span className="font-semibold">{t(lang, "spokenOn")}</span>
                        <span>{new Date(c.spoken_at).toLocaleDateString(LOCALES[lang])}</span>
                    </p>
                )}
                {editing ? (
                    <div className="space-y-1.5 mt-1">
                        <Textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={2}
                            data-testid={`edit-comment-text-${c.id}`}
                            className="text-sm"
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1.5">
                                <CalIcon className="w-3 h-3 text-neutral-500" />
                                <Input
                                    type="date"
                                    value={spokenAt}
                                    onChange={(e) => setSpokenAt(e.target.value)}
                                    data-testid={`edit-comment-spoken-${c.id}`}
                                    className="h-7 w-[150px] text-xs"
                                />
                                {spokenAt && (
                                    <button
                                        type="button"
                                        onClick={() => setSpokenAt("")}
                                        className="text-[10px] text-neutral-500 hover:text-red-600 underline"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-1">
                                <Button size="sm" onClick={save} data-testid={`save-comment-${c.id}`} className="h-7 px-2 text-xs bg-neutral-900 hover:bg-neutral-800">
                                    <Save className="w-3 h-3 mr-1" />{t(lang, "save")}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setText(c.text); setSpokenAt(c.spoken_at ? c.spoken_at.slice(0, 10) : ""); }} className="h-7 px-2 text-xs">
                                    {t(lang, "cancel")}
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-neutral-800 mt-0.5 whitespace-pre-wrap break-words">
                        {renderCommentText(c.text)}
                    </p>
                )}
            </div>
        </div>
    );
}

function PriceEditor({ task, lang, onSave }) {
    const [price, setPrice] = useState(task.price ?? "");

    useEffect(() => {
        setPrice(task.price ?? "");
    }, [task.id, task.price]);

    const original = task.price ?? "";
    const dirty = String(price) !== String(original);

    const commit = () => {
        if (!dirty) return;
        const num = price === "" ? null : parseFloat(price);
        onSave({ price: Number.isFinite(num) ? num : null, currency: num != null ? "EUR" : null }, true);
    };

    const clear = () => {
        setPrice("");
        onSave({ price: null, currency: null }, true);
    };

    return (
        <div className="grid grid-cols-[1fr,84px] gap-1.5">
            <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                data-testid="task-price-input"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                placeholder="0.00"
            />
            <div className="h-10 rounded-md border border-neutral-200 bg-neutral-50 px-3 flex items-center font-medium text-sm" data-testid="task-currency-select">€ EUR</div>
            {task.price != null && (
                <button onClick={clear} data-testid="task-price-clear" className="col-span-2 text-[10px] text-neutral-500 hover:text-red-600 text-left">
                    {lang === "tr" ? "fiyatı temizle" : lang === "de" ? "Preis löschen" : "clear price"}
                </button>
            )}
        </div>
    );
}

function SubtaskRow({ st, allUsers, onOpen, onToggleComplete, lang }) {
    const assignees = (st.assignee_ids || []).map((id) => allUsers.find((u) => u.id === id)).filter(Boolean);
    const completed = st.status === "completed";
    return (
        <div
            onClick={onOpen}
            data-testid={`subtask-${st.id}`}
            className={`w-full flex items-center gap-2 p-2.5 border rounded-md transition cursor-pointer group ${completed ? "bg-emerald-50/60 border-emerald-200 hover:border-emerald-300" : "bg-white border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300"}`}
        >
            <input
                type="checkbox"
                checked={completed}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => { e.stopPropagation(); onToggleComplete(); }}
                data-testid={`subtask-checkbox-${st.id}`}
                className="w-4 h-4 rounded border-neutral-300 accent-emerald-600 cursor-pointer shrink-0"
            />
            <Badge variant="outline" className={`uppercase tracking-wider text-[9px] font-bold px-1.5 py-0 h-5 ${STATUS_STYLES[st.status]}`}>
                {t(lang, st.status)}
            </Badge>
            <span className={`text-sm flex-1 truncate ${completed ? "line-through text-emerald-700/70" : "text-neutral-800"}`}>{st.title}</span>
            <div className="flex -space-x-1">
                {assignees.slice(0, 2).map((u) => (
                    <div key={u.id} title={u.name} className="w-5 h-5 rounded-full bg-neutral-900 text-white text-[9px] font-semibold flex items-center justify-center border border-white">
                        {u.name?.slice(0, 2)?.toUpperCase()}
                    </div>
                ))}
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-neutral-400 group-hover:text-neutral-700 shrink-0" />
        </div>
    );
}
